import { User, UserRole, LoginCredentials } from '../types/auth';
import mockUsers from '../mock/users.json';

const STORAGE_KEY_USER = 'nightlife_reporting_user';
const STORAGE_KEY_ROLE = 'nightlife_reporting_role';
const STORAGE_KEY_USER_ID = 'nightlife_reporting_user_id';

const GRAPHQL_URL = process.env.REACT_APP_CMS_GRAPHQL_URL || 'https://api-dev.nightlife.net/graphql';

export class AuthService {
  /**
   * Helper to map GraphQL roles array to standard UserRole.
   */
  private static mapGraphQLRoles(rolesList?: Array<{ id: number; name: string }>): UserRole {
    if (!rolesList || rolesList.length === 0) return 'Advertiser';
    const names = rolesList.map((r) => r.name.toLowerCase());
    if (names.includes('admin')) return 'Admin';
    if (names.includes('supervisor')) return 'Supervisor';
    return 'Advertiser';
  }

  /**
   * Validates credentials against GraphQL mutation Login via local proxy and persists session in cookies.
   */
  public static async login(credentials: LoginCredentials): Promise<User> {
    const loginQuery = `
      mutation Login($input: LoginInput!) {
        login(input: $input) {
          user {
            id
            email
            full_name
            roles {
              id
              name
            }
          }
          errors {
            message
          }
        }
      }
    `;

    try {
      const response = await fetch(GRAPHQL_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apollo-require-preflight': 'true',
        },
        credentials: 'include',
        body: JSON.stringify({
          query: loginQuery,
          variables: {
            input: {
              email: credentials.email,
              password: credentials.password,
            },
          },
        }),
      });

      if (!response.ok) {
        if (response.status >= 500) {
          throw new Error(`Server Error (${response.status}):Internal Server Error. Please try again later.`);
        }
        const text = await response.text().catch(() => '');
        throw new Error(`Authentication failed (${response.status}): ${text || response.statusText}`);
      }

      const result = await response.json();
      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }
      if (result.data?.login?.errors && result.data.login.errors.length > 0) {
        throw new Error(result.data.login.errors[0].message);
      }

      const gqlUser = result.data?.login?.user;
      if (gqlUser) {
        const userRole = AuthService.mapGraphQLRoles(gqlUser.roles);
        const authenticatedUser: User = {
          id: String(gqlUser.id),
          name: gqlUser.full_name || gqlUser.email,
          email: gqlUser.email,
          password: '',
          role: userRole,
        };

        // Store user profile in local context (NO auth_token in localStorage)
        localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(authenticatedUser));
        localStorage.setItem(STORAGE_KEY_ROLE, userRole);
        localStorage.setItem(STORAGE_KEY_USER_ID, String(gqlUser.id));

        return authenticatedUser;
      }
    } catch (err: any) {
      if (err.message && !err.message.includes('fetch') && !err.message.includes('Failed to fetch')) {
        throw err;
      }
      console.warn('[AuthService] GraphQL login network fallback active:', err);
    }

    // Dev Fallback against mockUsers if offline
    const foundUser = (mockUsers as User[]).find(
      (u) => u.email.toLowerCase() === credentials.email.toLowerCase()
    );

    if (!foundUser) {
      throw new Error('Invalid email or password');
    }

    if (credentials.password && credentials.password !== foundUser.password) {
      throw new Error('Invalid email or password');
    }

    localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(foundUser));
    localStorage.setItem(STORAGE_KEY_ROLE, foundUser.role);
    localStorage.setItem(STORAGE_KEY_USER_ID, foundUser.id);

    return foundUser;
  }

  /**
   * Queries GraphQL query { me } directly to verify active session from server.
   * If session is expired or invalid for Admin/Supervisor, clears local role and returns null.
   */
  public static async fetchCurrentUserFromGraphQL(): Promise<User | null> {
    const meQuery = `
      query {
        me {
          id
          email
          full_name
          roles {
            id
            name
          }
        }
      }
    `;

    try {
      const response = await fetch(GRAPHQL_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apollo-require-preflight': 'true',
        },
        credentials: 'include',
        body: JSON.stringify({ query: meQuery }),
      });

      if (response.ok) {
        const result = await response.json();
        const gqlUser = result.data?.me;
        if (gqlUser) {
          const userRole = AuthService.mapGraphQLRoles(gqlUser.roles);
          const authenticatedUser: User = {
            id: String(gqlUser.id),
            name: gqlUser.full_name || gqlUser.email,
            email: gqlUser.email,
            password: '',
            role: userRole,
          };
          localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(authenticatedUser));
          localStorage.setItem(STORAGE_KEY_ROLE, userRole);
          localStorage.setItem(STORAGE_KEY_USER_ID, String(gqlUser.id));
          return authenticatedUser;
        }
      }
    } catch (e) {
      console.warn('[AuthService] Could not fetch user from GraphQL:', e);
    }

    // Session expired or invalid for executive role -> clear stored role if Admin/Supervisor
    const savedRole = localStorage.getItem(STORAGE_KEY_ROLE);
    if (savedRole === 'Admin' || savedRole === 'Supervisor') {
      AuthService.logout();
    }
    return null;
  }

  /**
   * Clears stored Admin/Supervisor role and session state.
   */
  public static logout(): void {
    localStorage.removeItem(STORAGE_KEY_USER);
    localStorage.removeItem(STORAGE_KEY_ROLE);
    localStorage.removeItem(STORAGE_KEY_USER_ID);
  }

  /**
   * Resets active session explicitly to Advertiser (usr_001 / Alex Advertiser).
   */
  public static resetToAdvertiser(): User {
    const defaultUser = (mockUsers as User[])[0];
    localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(defaultUser));
    localStorage.setItem(STORAGE_KEY_ROLE, defaultUser.role);
    localStorage.setItem(STORAGE_KEY_USER_ID, defaultUser.id);
    return defaultUser;
  }

  /**
   * Returns current authenticated user from localStorage.
   */
  public static getCurrentUser(): User {
    const savedUserStr = localStorage.getItem(STORAGE_KEY_USER);
    if (savedUserStr) {
      try {
        return JSON.parse(savedUserStr) as User;
      } catch (e) {
        console.error('Failed to parse user from localStorage:', e);
      }
    }

    return AuthService.resetToAdvertiser();
  }

  /**
   * Returns all available mock users.
   */
  public static async getUsers(): Promise<User[]> {
    return mockUsers as User[];
  }
}
